import {useCallback, useEffect} from 'react';
import {useEvent} from './use-event';

export type KeyDownOptions = {
  enabled?: boolean;
  metaKey?: boolean;
  ctrlKey?: boolean;
  shiftKey?: boolean;
  altKey?: boolean;
};

export function useKeyDown({
  key,
  cb,
  options,
}: {
  key: string | string[];
  cb: (event: KeyboardEvent) => void;
  options?: KeyDownOptions;
}) {
  const {
    enabled = true,
    metaKey = false,
    ctrlKey = false,
    shiftKey = false,
    altKey = false,
  } = options ?? {};
  const handleCallback = useEvent(cb);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      const keys = Array.isArray(key) ? key : [key];
      const isKeyMatch = keys.includes(event.key);
      const isModifierMatch =
        event.metaKey === metaKey &&
        event.ctrlKey === ctrlKey &&
        event.shiftKey === shiftKey &&
        event.altKey === altKey;

      if (isKeyMatch && isModifierMatch) {
        handleCallback(event);
      }
    },
    [key, handleCallback, metaKey, ctrlKey, shiftKey, altKey],
  );

  useEffect(() => {
    if (!enabled) return;

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown, enabled]);
}
